/*
 * Based on https://www.w3.org/WAI/ARIA/apg/example-index/disclosure/disclosure-navigation.html
 */

export default class Nav {
	/**
	 * @param {HTMLElement} element
	 */
	constructor(element) {
		this.element = element;
		this.elements = {
			firstLevelEntries: Array.from(
				this.element.querySelectorAll(".HeaderNav-entry--lvl1"),
			),
			toggles: Array.from(
				this.element.querySelectorAll(".HeaderNav-entry--button"),
			),
			menus: Array.from(this.element.querySelectorAll(".HeaderNav-list--lvl2")),
		};

		this.openIndex = null;

		this.elements.firstLevelEntries.forEach((entry) => {
			entry.addEventListener(
				"keydown",
				this.onFirstLevelEntryKeyDown.bind(this),
			);
		});

		this.elements.toggles.forEach((toggle, index) => {
			const menu = this.elements.menus[index];

			if (menu) {
				menu.addEventListener("keydown", this.onMenuKeyDown.bind(this));
				toggle.addEventListener("click", this.onButtonClick.bind(this));
				toggle.addEventListener("keydown", this.onButtonKeyDown.bind(this));
			}
		});

		this.element.addEventListener("focusout", this.onBlur.bind(this));
	}

	/**
	 * @param {KeyboardEvent} event
	 * @param {Array} elements
	 * @param {number} index
	 */
	static controlFocusByKey(event, elements, index) {
		const move = {
			start: "ArrowLeft",
			end: "ArrowRight",
		};
		if (event.target.closest("[dir]")?.dir === "rtl") {
			move.start = "ArrowRight";
			move.end = "ArrowLeft";
		}

		switch (event.key) {
			case "ArrowUp":
			case move.start:
				event.preventDefault();
				if (index > -1) {
					const prevIndex = Math.max(0, index - 1);
					elements[prevIndex].focus();
				}
				break;
			case "ArrowDown":
			case move.end:
				event.preventDefault();
				if (index > -1) {
					const nextIndex = Math.min(elements.length - 1, index + 1);
					elements[nextIndex].focus();
				}
				break;
			case "Home":
				event.preventDefault();
				elements[0].focus();
				break;
			case "End":
				event.preventDefault();
				elements[elements.length - 1].focus();
				break;
			default:
				break;
		}
	}

	/**
	 * @param {Event} event
	 * @param {HTMLElement} event.relatedTarget
	 * @returns {void}
	 */
	onBlur({ relatedTarget }) {
		const elementContainsFocus = this.element.contains(relatedTarget);

		if (!elementContainsFocus) {
			this.close(this.openIndex);
		}
	}

	/**
	 * @param {Event} event
	 * @param {HTMLElement} event.target
	 * @returns {void}
	 */
	onButtonClick({ target }) {
		const button = target.closest("button");
		const buttonIndex = this.elements.toggles.indexOf(button);
		const isExpanded = button.getAttribute("aria-expanded") === "true";

		this.toggleMenu(buttonIndex, !isExpanded);
	}

	/**
	 * @param {KeyboardEvent} event
	 * @returns {void}
	 */
	onButtonKeyDown(event) {
		const button = event.target.closest("button");
		const buttonIndex = this.elements.toggles.indexOf(button);

		// close on escape
		if (event.key === "Escape") {
			this.close(this.openIndex);
		}

		// move focus into the open menu if the current menu is open
		else if (this.openIndex === buttonIndex && event.key === "ArrowDown") {
			event.preventDefault();
			this.elements.menus[this.openIndex].querySelector("a").focus();
		}
	}

	/**
	 * @param {KeyboardEvent} event
	 */
	onFirstLevelEntryKeyDown(event) {
		const entries = this.elements.firstLevelEntries;
		const index = entries.indexOf(document.activeElement);

		// Handle arrow key navigation for first level entries
		Nav.controlFocusByKey(event, entries, index);
	}

	/**
	 * @param {KeyboardEvent} event
	 * @returns {void}
	 */
	onMenuKeyDown(event) {
		if (this.openIndex === null) {
			return;
		}

		const links = Array.from(
			this.elements.menus[this.openIndex].querySelectorAll("a"),
		);
		const index = links.indexOf(document.activeElement);

		// close on escape
		if (event.key === "Escape") {
			this.elements.toggles[this.openIndex].focus();
			this.close(this.openIndex);
		}

		// handle arrow key navigation within menu links
		else {
			Nav.controlFocusByKey(event, links, index);
		}
	}

	/**
	 * @param {number} index
	 * @param {boolean} expanded
	 * @returns {void}
	 */
	toggleMenu(index, expanded) {
		// if another menu is already open, close that one
		if (this.openIndex !== null && this.openIndex !== index) {
			this.close(this.openIndex);
		}

		if (expanded) {
			this.open(index);
		} else {
			this.close(index);
		}
	}

	/**
	 * @param {number} index
	 * @returns {void}
	 */
	open(index) {
		if (index === null) return;

		this.openIndex = index;
		this.elements.toggles[index].setAttribute("aria-expanded", "true");
	}

	/**
	 * @param {number} index
	 * @returns {void}
	 */
	close(index) {
		if (index === null) return;

		this.openIndex = null;
		this.elements.toggles[index].setAttribute("aria-expanded", "false");
	}
}
